import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import PageBanner from 'components/Luxkit/Banners/PageBanner'
import LineCheckCircleIcon from 'components/Luxkit/Icons/line/LineCheckCircleIcon'
import LineExclamationTriangleIcon from 'components/Luxkit/Icons/line/LineExclamationTriangleIcon'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import ModalContext from 'contexts/ModalContext'
import useTargetedBanner from 'hooks/FeatureFlag/useTargetedBanner'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import switchFunc from 'lib/function/switchFunc'
import { getDismissedBannerIds, isBannerDismissed, recordSiteBannerDismissal } from 'storage/siteBanner'
import NotificationBannerModal from './NotificationBannerModal'

const getBannerKind = switchFunc<React.ComponentProps<typeof PageBanner>['kind'], App.TargetedBannerCampaign['bannerType']>({
  warning: 'warning',
  error: 'critical',
  success: 'promotional-primary',
}, 'informational-primary')

const getBannerIcon = switchFunc<React.ComponentProps<typeof PageBanner>['icon'], App.TargetedBannerCampaign['bannerType']>({
  warning: <LineExclamationTriangleIcon />,
  error: <LineExclamationTriangleIcon />,
  success: <LineCheckCircleIcon />,
}, <LineInfoCircleIcon />)

export default function NotificationBannerV3() {
  const campaigns = useTargetedBanner()

  const dismissedBannerIds = getDismissedBannerIds()
  const banner = useMemo(() => campaigns?.find(b => !dismissedBannerIds.has(b.id.toString())), [campaigns, dismissedBannerIds])

  // TODO
  const [isDismissed, setIsDismissed] = useState(false)

  useEffect(() => {
    if (banner) {
      setIsDismissed(isBannerDismissed(banner.id.toString()))
    }
  }, [banner])

  useEffect(() => {
    if (banner) {
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          setIsDismissed(isBannerDismissed(banner.id.toString()))
        }
      }

      document.addEventListener('visibilitychange', handleVisibilityChange)

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }
  }, [banner])

  const containerRef = useRef<HTMLDivElement>(null)
  useAppHeaderObserver(containerRef)

  const showModal = useContext(ModalContext)

  const openBannerModal = useCallback(() => {
    if (banner) {
      showModal(<NotificationBannerModal title={banner.modalTitle} content={banner.modalContent} />)
    }
  }, [banner, showModal])

  const dismiss = useCallback(() => {
    if (banner) {
      setIsDismissed(true)
      recordSiteBannerDismissal(banner.id.toString(), banner.endDate)
    }
  }, [banner])

  if (!banner || isDismissed) {
    return null
  }

  return <PageBanner
    ref={containerRef}
    id="notification-page-banner"
    kind={getBannerKind(banner.bannerType)}
    icon={getBannerIcon(banner.bannerType)}
    description={<>
      <b>{banner.boldText}</b>{banner.bannerText}
    </>}
    onDismiss={dismiss}
    tabletAlign="center"
    primaryActionButtonProps={banner.modalContent ? { onClick: openBannerModal, children: 'Read more' } : undefined}
  />
}
